import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  FileCheck,
  Clock,
  Pencil,
  Send,
  Archive,
  AlertTriangle,
  History,
  User,
} from 'lucide-react'
import type { ReportType, VendorReviewListItem } from '@common/types'
import { REPORT_TYPE_LABELS } from '@common/types'
import {
  fetchAuditor,
  fetchAuditorReviews,
  fetchAuditorHistory,
  publishAuditorApi,
  archiveAuditorApi,
} from '@frontend/lib/api'
import { formatDate, formatDateTime } from '@frontend/lib/utils'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { AuditorStatusBadge, ReviewStatusBadge, ValidityBadge } from '@frontend/components/ui/status-badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'
import { AuditorModal } from '@frontend/components/auditors/AuditorModal'
import { EditHistoryTimeline } from '@frontend/components/history'
import { MetadataItem } from '@frontend/components/ui/metadata-item'
import { isHttpUrl } from '@frontend/lib/utils'

export function AuditorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['auditor', id],
    queryFn: () => fetchAuditor(id!),
    enabled: !!id,
  })

  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['auditor-reviews', id],
    queryFn: () => fetchAuditorReviews(id!),
    enabled: !!id,
  })

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['auditor-history', id],
    queryFn: () => fetchAuditorHistory(id!),
    enabled: !!id,
  })

  const publishMutation = useMutation({
    mutationFn: () => publishAuditorApi(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditor', id] })
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      queryClient.invalidateQueries({ queryKey: ['auditor-history', id] })
      // Invalidate review queries so linked reviews pick up the new auditor status
      queryClient.invalidateQueries({ queryKey: ['review'] })
      toast.success('Auditor published')
    },
    onError: () => toast.error('Failed to publish auditor'),
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveAuditorApi(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditor', id] })
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      queryClient.invalidateQueries({ queryKey: ['auditor-history', id] })
      queryClient.invalidateQueries({ queryKey: ['review'] })
      setArchiveOpen(false)
      toast.success('Auditor archived')
    },
    onError: () => toast.error('Failed to archive auditor'),
  })

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  if (error || !data) {
    return (
      <AppLayout>
        <div className="py-16 text-center">
          <p className="text-destructive">Failed to load auditor.</p>
          <Button variant="ghost" className="mt-4" onClick={() => navigate('/auditors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Auditors
          </Button>
        </div>
      </AppLayout>
    )
  }

  const auditor = data.auditor
  const reviews = reviewsData?.reviews ?? []
  const isArchived = auditor.status === 'archived'

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Back link */}
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => navigate('/auditors')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Auditors
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">{auditor.name}</h1>
              <AuditorStatusBadge status={auditor.status} />
            </div>
            {auditor.website && isHttpUrl(auditor.website) && (
              <a
                href={auditor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {new URL(auditor.website).hostname}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {!isArchived && (
            <div className="flex items-center gap-2">
              {can('auditors.edit') && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              {can('auditors.publish') && auditor.status === 'draft' && auditor.opinion && (
                <Button size="sm" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
                  <Send className="h-4 w-4 mr-1" />
                  Publish
                </Button>
              )}
              {can('auditors.archive') && (
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setArchiveOpen(true)}>
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
              )}
            </div>
          )}
        </div>

        {isArchived && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-800 rounded-md text-sm border border-amber-200">
            <AlertTriangle className="h-4 w-4" />
            This auditor was archived{auditor.archivedBy ? ` by ${auditor.archivedBy}` : ''} on{' '}
            {formatDate(auditor.archivedAt)}.
          </div>
        )}

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <MetadataItem
              icon={Globe}
              label="Website"
              value={
                auditor.website && isHttpUrl(auditor.website) ? (
                  <a
                    href={auditor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {auditor.website} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  'Not provided'
                )
              }
            />
            <MetadataItem icon={Clock} label="Created" value={formatDateTime(auditor.createdAt)} />
            <MetadataItem icon={User} label="Created By" value={auditor.createdBy || '—'} />
            <MetadataItem
              icon={FileCheck}
              label="Last Published"
              value={
                auditor.lastPublishedAt
                  ? `${formatDateTime(auditor.lastPublishedAt)}${auditor.lastPublishedBy ? ` by ${auditor.lastPublishedBy}` : ''}`
                  : 'Never published'
              }
            />
          </CardContent>
        </Card>

        {/* Opinion Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Riveron&apos;s Opinion</CardTitle>
          </CardHeader>
          <CardContent>
            {auditor.opinion ? (
              <p className="text-sm whitespace-pre-wrap">{auditor.opinion}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No opinion has been set yet. Edit this auditor to add one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Linked Reviews */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Linked Reviews ({reviews.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {reviewsLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : reviews.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No reviews linked to this auditor yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Validity</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Documents</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review: VendorReviewListItem) => {
                    const reportTypeLabel = review.reportType
                      ? REPORT_TYPE_LABELS[review.reportType as ReportType] || review.reportType
                      : null
                    const label = [review.vendorName, reportTypeLabel].filter(Boolean).join(' — ')
                    return (
                      <TableRow key={review.id}>
                        <TableCell>
                          {label ? (
                            <a
                              href={`/reviews/${review.id}`}
                              onClick={(e) => {
                                e.preventDefault()
                                navigate(`/reviews/${review.id}`)
                              }}
                              className="text-primary hover:underline"
                            >
                              {label}
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <ReviewStatusBadge status={review.status} />
                        </TableCell>
                        <TableCell>
                          <ValidityBadge auditPeriodEnd={review.auditPeriodEnd} />
                        </TableCell>
                        <TableCell>{formatDate(review.createdAt)}</TableCell>
                        <TableCell>{review.documentCount}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-4 w-4" />
              Edit History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <EditHistoryTimeline
                edits={historyData?.edits ?? []}
                emptyMessage="No changes have been recorded for this auditor yet."
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      <AuditorModal mode="edit" open={editOpen} onOpenChange={setEditOpen} auditor={auditor} />

      {/* Archive Confirmation */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Auditor</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive &ldquo;{auditor.name}&rdquo;? This will hide the auditor from the list.
              Existing review links will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
